/**
 * FEAT-097 — display logic for the Buffer channels list on the Social
 * Accounts page. React-free so the node test runner can import it.
 */
import type { ApiError } from "./api-error";

/** GET /agencyclients/:id/social-accounts/buffer/channels row. */
export interface BufferChannelRow {
  buffer_channel_id: string;
  name: string;
  service: string;
  /** CCM's platform for this channel, or null when CCM can't import it yet. */
  platform: string | null;
  already_imported: boolean;
}

export type ChannelState = "available" | "imported" | "unsupported";

export function channelState(row: Pick<BufferChannelRow, "platform" | "already_imported">): ChannelState {
  if (row.already_imported) return "imported";
  if (row.platform == null) return "unsupported";
  return "available";
}

export type ChannelsErrorDescription = { kind: "no_key" } | { kind: "error"; text: string };

/**
 * 409 no_credentials is the ordinary state of a workspace that doesn't use
 * Buffer — shown as a hint, not a failure.
 */
export function describeChannelsError(err: ApiError): ChannelsErrorDescription {
  if (err.status === 409) return { kind: "no_key" };
  return { kind: "error", text: describeApiErrorMessage(err) };
}

// Readable text for error codes that never carry a `message` field (only
// provider_error does, from Buffer's own response) — a plain error code
// like "already_imported" isn't something a founder should have to parse.
const KNOWN_ERROR_TEXT: Record<string, string> = {
  already_imported: "That channel is already imported.",
  unsupported_platform: "CCM doesn't support importing that platform yet.",
  not_found: "That channel is no longer in your Buffer account.",
};

export function describeApiErrorMessage(err: ApiError): string {
  try {
    const parsed = JSON.parse(err.body) as { error?: unknown; message?: unknown };
    if (typeof parsed.message === "string") return parsed.message;
    if (typeof parsed.error === "string" && KNOWN_ERROR_TEXT[parsed.error]) return KNOWN_ERROR_TEXT[parsed.error];
  } catch {
    // Not JSON — fall back to ApiError's own message.
  }
  return err.message;
}
